/* eslint-disable prettier/prettier */
import { useState, useEffect, useRef, useMemo } from 'react'
import { inject, observer } from 'mobx-react'
import { useNavigate, useLocation } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { Box, Button, HStack, PinInput, PinInputField } from '@chakra-ui/react'
import { openDeepLink, format } from 'utils'

import TurnstileView from 'components/Trunstile'
import Timer from 'components/Timer'
import { PrimaryBtn } from 'components/button'
import { Invalid } from 'components/form'
import { config } from 'config'

const ErrorMessage = {
  en: 'This OTP is invalid or expired. Please try again.',
  th: 'รหัส OTP ไม่ถูกต้องหรือหมดอายุ กรุณาลองใหม่อีกครั้ง',
}

const VerifyOtp = (props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const state = location?.state || {}
  const isPinExist = state.isPinExist
  const verifyType = state.verifyType

  const [loading, setLoading] = useState(false)
  const [loadingResend, setLoadingResend] = useState(false)
  const [otpCodeInput, setOtpCodeInput] = useState('')
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [prefix, setPrefix] = useState('')
  const [initialMobile, setInitialMobile] = useState('')
  const [refCode, setRefCode] = useState('')
  const [delayResend, setDelayResend] = useState(
    config.otp.resend_request_second_time
  )
  const [timerExpire, setTimerExpire] = useState(config.otp.expire_second_time)
  const [errorMessage, setErrorMessage] = useState('')

  const { language } = props

  const codeRef = useRef([])
  const isWatchSMS = useRef(true)

  useEffect(() => {
    if (state.mobile) {
      setMobile(state.mobile || '-')
      setPrefix(state?.prefix || '-')
      setInitialMobile(state?.initialMobile || '-')
      setRefCode(state?.refCode || '-')
      props.language.setLanguage(state?.lang || 'th')
    }
    if (state.email) {
      setEmail(state.email || '-')
      setRefCode(state?.refCode || '-')
      props.language.setLanguage(state?.lang || 'th')
    }
  }, [state, props.language])

  const clearRef = (isWatch) => {
    codeRef.current = []
    isWatchSMS.current = isWatch
  }

  const onSubmit = async () => {
    setLoading(true)

    try {
      let verifyResponse;
      if (verifyType === 'email') {
        const requestData = {
          email,
          otp: otpCodeInput,
          refCode,
        }
        verifyResponse = await props.otp.verifyEmailOtp(requestData)
      } else {
        const requestData = {
          mobile,
          otp: otpCodeInput,
          refCode,
        }
        verifyResponse = await props.otp.verifyOtp(requestData)
      }

      if (verifyResponse) {
        setLoading(false)
        if (!isPinExist) {
          const payload = {
            otpToken: verifyResponse.otpToken,
            isPreRegister: true,
          }

          const query = Object.keys(payload)
            .map((key) => {
              return `${key}=${encodeURIComponent(payload[key])}`
            })
            .join('&')

          openDeepLink('createPinCodeSet', query)
        } else {
          navigate('/pin', {
            state: {
              lang: location.state.lang,
              otpToken: verifyResponse.otpToken,
              clientId: location.state.clientId,
              redirectUri: location.state.redirectUri,
              state: location.state.state,
              codeChallenge: location.state.codeChallenge,
              mobile,
              refCode,
            },
          })
        }
      } else {
        setLoading(false)
        const errorTest =
          timerExpire <= 0
            ? language.getMsg(
              'This OTP has expired. Please request again.',
              'รหัส OTP นี้หมดอายุแล้ว กรุณาขอรหัสอีกครั้ง'
            )
            : language.getMsg(ErrorMessage.en, ErrorMessage.th)
        setErrorMessage(errorTest)
      }
    } catch (error) {
      setLoading(false)
      const errorTest = language.getMsg(ErrorMessage.en, ErrorMessage.th)
      setErrorMessage(errorTest)
      setOtpCodeInput('')
    }
  }

  useEffect(() => {
    if (timerExpire <= 0) {
      const message = language.getMsg(
        'This OTP is invalid. Please try again.',
        'รหัส OTP ไม่ถูกต้องหรือหมดอายุ กรุณาลองใหม่อีกครั้ง'
      )
      setErrorMessage(message)
    }
  }, [timerExpire, language])

  useEffect(() => {
    const timerTimerExpire = setInterval(() => {
      setTimerExpire(timerExpire - 1)
    }, 1000)

    const timerDelayResend = setInterval(() => {
      setDelayResend(delayResend - 1)
    }, 1000)

    if (delayResend === 0) {
      clearInterval(timerDelayResend)
    }

    if (timerExpire === 0) {
      clearInterval(timerTimerExpire)
    }

    return () => {
      clearInterval(timerDelayResend)
      clearInterval(timerTimerExpire)
    }
  })

  const onTurnstile = (token) => {
    setTurnstileToken(token)
  }

  const onExpire = () => {
    setTurnstileToken('')
  }

  const resendRequestOtp = async () => {
    setLoadingResend(true)
    try {
      let requestOtpResponse;
      if (verifyType === 'email') {
        requestOtpResponse = await props.otp.requestEmailOtpRequest({
          email,
          token: turnstileToken,
        })
      } else {
        requestOtpResponse = await props.otp.requestOtpRequest({
          mobile,
          token: turnstileToken,
        })
      }

      if (requestOtpResponse) {
        setOtpCodeInput('')
        setErrorMessage('')
        setRefCode(requestOtpResponse.refcode)
        setDelayResend(config.otp.resend_request_second_time)
        setTimerExpire(config.otp.expire_second_time)
      }
    } catch (e) {
      console.log('e:', e.message)
    }

    setLoadingResend(false)
  }

  const onOtpChange = (val) => {
    setErrorMessage('')

    // manual input. (especially, erasing)
    if (!isWatchSMS.current) {
      setOtpCodeInput(val)
      return
    }

    // Compatible with other browser input, like Chrome or WeChat.
    if (val.length === config.otp.code_max_len) {
      setOtpCodeInput(val)
      clearRef(false)
      return
    }

    // Compatible with safari sms autocomplete.
    codeRef.current.push(val.slice(-1))
    if (codeRef.current.length === config.otp.code_max_len) {
      setOtpCodeInput(codeRef.current.join(''))
      clearRef(false)
      return
    }

    // manual while waiting sms
    setOtpCodeInput(val)
  }

  const sizePinInputField = { base: '20', sm: '12', md: '20' }
  const isExpired = delayResend <= 0
  const content = isExpired ? (
    <HStack paddingBottom={4} paddingTop={4} justifyContent="center">
      {turnstileToken === '' ? (
        <LinkDisabled>
          {language.getMsg('Request again', 'ขอรหัสอีกครั้ง')}
        </LinkDisabled>
      ) : (
        <Button
          color={theme.color.electricBlue}
          fontSize={16}
          fontWeight={600}
          onClick={resendRequestOtp}
          variant="link"
          isLoading={loadingResend}
        >
          {language.getMsg('Request again', 'ขอรหัสอีกครั้ง')}
        </Button>
      )}
    </HStack>
  ) : (
    <HStack paddingBottom={4} justifyContent="center">
      <ResendIn>
        {language.getMsg('Request OTP in', 'ขอรหัสได้อีกครั้งใน')}
      </ResendIn>
      <Timer delayResend={delayResend} />
      <DescBlue>{language.getMsg('mins', 'นาที')}</DescBlue>
    </HStack>
  )

  const isInputMax = otpCodeInput?.length !== config.otp.code_max_len
  const disabledBtn = isInputMax || timerExpire <= 0
  const disabled = isExpired
    ? disabledBtn || turnstileToken === ''
    : disabledBtn

  const turnstile = isExpired ? (
    <TurnstileSection>
      <TurnstileView onVerify={onTurnstile} onExpire={onExpire} />
    </TurnstileSection>
  ) : undefined

  const verifyTo = useMemo(() => verifyType === 'email' ? email
    : `${prefix} ${format.formatMobilePattern(
      initialMobile
    )}`, [email, initialMobile, prefix, verifyType])

  return (
    <PageView>
      <Title>
        {language.getMsg('Verify your account', 'ยืนยันบัญชีของคุณ')}
      </Title>
      <Desc>
        {language.getMsg(
          `Code is sent to ${verifyTo}`,
          `ส่งรหัสยืนยันไปที่ ${verifyTo}`)}
      </Desc>

      <Box paddingTop={16}>
        <HStack justifyContent="center">
          <PinInput
            value={otpCodeInput}
            size="lg"
            // onComplete={(value: string) => onSubmitOtpVerify(value)}
            onChange={onOtpChange}
            placeholder="-"
            colorScheme="blue"
            isDisabled={timerExpire <= 0}
            type="number"
            otp={true}
          >
            <StyledPinInputField
              w={sizePinInputField}
              h={sizePinInputField}
              type="number"
              inputMode="numeric"
              isActive={otpCodeInput?.[0]}
              activeBorder={theme.input.activeBorder}
              activeBorderV2={theme.input.activeBorderV2}
              errorMessage={errorMessage}
            />
            <StyledPinInputField
              w={sizePinInputField}
              h={sizePinInputField}
              type="number"
              inputMode="numeric"
              isActive={otpCodeInput?.[1]}
              activeBorder={theme.input.activeBorder}
              activeBorderV2={theme.input.activeBorderV2}
              errorMessage={errorMessage}
            />
            <StyledPinInputField
              w={sizePinInputField}
              h={sizePinInputField}
              type="number"
              inputMode="numeric"
              isActive={otpCodeInput?.[2]}
              activeBorder={theme.input.activeBorder}
              activeBorderV2={theme.input.activeBorderV2}
              errorMessage={errorMessage}
            />
            <StyledPinInputField
              w={sizePinInputField}
              h={sizePinInputField}
              type="number"
              inputMode="numeric"
              isActive={otpCodeInput?.[3]}
              activeBorder={theme.input.activeBorder}
              activeBorderV2={theme.input.activeBorderV2}
              errorMessage={errorMessage}
            />
          </PinInput>
        </HStack>
      </Box>

      {errorMessage ? (
        <Box paddingTop={2} justifyContent="center">
          <Invalid textAlign="center" invalid={errorMessage} text />
        </Box>
      ) : undefined}

      {turnstile}

      <BottomContainer>
        <RedCode>
          {`${language.getMsg('Ref. code', 'รหัสอ้างอิง')} ${refCode}`}
        </RedCode>
        {content}
        <Btn>
          <PrimaryBtn
            loading={loading}
            text={language.getMsg('Verify', 'ยืนยัน')}
            disabled={disabled}
            onClick={onSubmit}
          />
        </Btn>
      </BottomContainer>
    </PageView>
  )
}

const PageView = styled.div`
  width: 100%;
  max-width: 480px;
  min-height: 86vh;
  margin: 0 auto;
  padding: 20px 38px;
  margin-top: 24px;
  position: relative;

  ${(p) => p.theme.media.mobile_xs} {
    padding: 10px;
  }
`

const Title = styled.div`
  font-weight: 600;
  font-size: 28px;
  line-height: 35px;
  color: #02114d;

  ${(p) => p.theme.media.mobile_xs} {
    font-size: 25px;
  }
`

const Desc = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #323a43;
  padding-top: 16px;
`

const TurnstileSection = styled.div`
  padding-top: 32px;
`

const ResendIn = styled(Desc)`
  padding-top: 8px;
`

const LinkDisabled = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: ${(p) => p.theme.color.disable};
`

const DescBlue = styled(ResendIn)`
  font-weight: 600;
  color: #003cde;
`

const RedCode = styled.div`
  width: 100%;
  text-align: center;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;

  color: #323a43;
`

const BottomContainer = styled.div`
  width: 100%;
  margin-top: 65px;
`

const Btn = styled.div`
  width: 240px;
  margin: 0 auto;
  margin-top: 26px;
  margin-bottom: 24px;
`
const StyledPinInputField = styled(PinInputField)`
  border-color: ${(props) =>
    props.errorMessage ? '#F95247' : props.isActive && props.activeBorderV2};
  font-weight: ${(props) => (props.isActive ? 600 : 400)};
  color: ${(props) => props.activeBorder};
  font-size: 20;
`

export default inject('otp', 'language')(observer(VerifyOtp))
